import { addBusinessDays, endOfDay, isWithinInterval, startOfDay, subBusinessDays } from 'date-fns';
import { useMemo } from 'react';

import { TaskStatus } from '@ndvr/customer-api';

import { convertBackendTimeToBrowser, parseLocalDate } from '@/utils';
import { useCalendarViews } from 'views/calendar/calendar-queries';
import { useTasksForPriorityWindow } from 'views/tasks/task-queries';

import { Priority, PriorityType } from './priority-types';

export const useAllPriorities = () => {
    const { now, past2BusinessDays, future2BusinessDays } = useMemo(() => {
        const currentTime = new Date();
        const today = startOfDay(currentTime);

        return {
            now: currentTime,
            past2BusinessDays: startOfDay(subBusinessDays(today, 2)),
            future2BusinessDays: endOfDay(addBusinessDays(today, 2)),
        };
    }, []);

    const { data: calendarData, isLoading: eventsLoading } = useCalendarViews({
        fromDate: past2BusinessDays,
        toDate: future2BusinessDays,
    });

    const { data: tasks, isLoading: tasksLoading } = useTasksForPriorityWindow(future2BusinessDays);

    const priorities = useMemo(() => {
        const result: Priority[] = [];

        if (calendarData?.calendarEvents) {
            for (const event of calendarData.calendarEvents) {
                if (!event.clientHouseholdGUID) {
                    // info: Only show calendar events that are linked to clients
                    continue;
                }

                const startTime = convertBackendTimeToBrowser(event.startTime, event.startTimeZone);

                if (isWithinInterval(startTime, { start: now, end: future2BusinessDays })) {
                    result.push({
                        type: PriorityType.EVENT,
                        data: event,
                    });
                } else if (isWithinInterval(startTime, { start: past2BusinessDays, end: now })) {
                    result.push({
                        type: PriorityType.FOLLOWUP,
                        data: event,
                    });
                }
            }
        }

        if (tasks) {
            for (const task of tasks) {
                if (task.overdue || task.status === TaskStatus.COMPLETED) {
                    continue;
                }

                const dueDate = parseLocalDate(task.dueDate);

                if (isWithinInterval(dueDate, { start: past2BusinessDays, end: future2BusinessDays })) {
                    result.push({
                        type: PriorityType.TASK,
                        data: task,
                    });
                }
            }
        }

        return result;
    }, [calendarData, future2BusinessDays, now, past2BusinessDays, tasks]);

    return {
        data: priorities,
        isPending: eventsLoading || tasksLoading,
    };
};
